import { setByText } from '@features/feature-flags';

import type { ComponentCardState, ComponentMemberFlag } from './component-card';

/**
 * Every sentence one museum's Components tab says, in one place.
 *
 * Out here rather than inside the components, for the reason
 * `grant-provenance-copy.ts` and `component-error-copy.ts` give: `apps/web` runs
 * vitest on `node` with no jsdom, so a sentence a component assembles for itself
 * is a sentence no test can read — and the sentences below are the ones an
 * operator has never read before, which makes them the ones worth pinning down.
 *
 * Kept apart from `component-card.ts` on the same grounds the card state is kept
 * apart from the card: one module decides *what is true*, this one decides *how
 * to say it*, and rewording a card must not be able to change which card it is.
 */

/** What one card says, and what it offers. */
export interface ComponentCardCopy {
    /** The state, in a few words — the badge above the description. */
    readonly status: string;
    /**
     * What this position means for **selling**, or `undefined` when the badge
     * has already said it.
     *
     * Optional rather than always-present, which is the shape of the decision:
     * this card is where an operator decides whether to put a client on a
     * component, and a line that only restates the badge is one they learn to
     * skip — taking the lines that do carry something with it. What the
     * component *is* is rendered from the catalog row beside this, in every
     * position, because that is the part they are selling.
     */
    readonly description?: string;
    /**
     * The button's label, or `undefined` when there is no honest button to
     * offer. Every state has one today: `not-activatable`, the one that did
     * not, went with `D43` — a component with no member flags is activatable
     * now, and its holding is recorded by the subscription row rather than by
     * a grant. Optional still, because the panel renders no button for an
     * absent label and the next state that must offer none should find that
     * path intact.
     */
    readonly action?: string;
}

export interface ComponentCardCopyInput {
    readonly state: ComponentCardState;
    /** From `ComponentCard.outstanding`. Only read in the `on-outstanding` state. */
    readonly outstanding: number;
    readonly displayName: string;
}

export function componentCardCopy(input: ComponentCardCopyInput): ComponentCardCopy {
    const { state, outstanding } = input;

    switch (state) {
        /**
         * It used to say *"holds every flag in this component"*, and under
         * `D32` that was false as written. Held-ness is **any** member carrying
         * this component's grant (`component-card.ts:168-178`), so a
         * half-completed activation reads as held and this sentence asserted
         * something the state function has never checked. What each member
         * actually resolves to is now rendered per flag, by
         * {@link memberShortfallNotice} and the member list beside it, which is
         * where a claim about individual flags can be made honestly.
         */
        case 'on':
            return { status: 'Active', action: 'Deactivate' };

        /**
         * Active *and* unfinished, and the badge has to carry both: the museum
         * is entitled — the product works for them today — while some setting is
         * still waiting on somebody. An operator reading only "Active" would
         * never go and finish it.
         */
        case 'on-outstanding':
            return {
                status: `Active · ${String(outstanding)} ${outstanding === 1 ? 'setting' : 'settings'} outstanding`,
                description: 'Some of its setup is still outstanding — the checklist says what.',
                action: 'Deactivate',
            };

        /**
         * The state that earns this module its keep. Deactivation takes the
         * grants and keeps the rows, so everything this museum configured is
         * still sitting there. "Off" on its own would send an operator to
         * re-provision work that was never lost.
         */
        case 'off-provisioned':
            return {
                status: 'Off · previously set up',
                description:
                    'They had it before and their setup was kept, so selling it again turns ' +
                    'on what is already there rather than starting over.',
                action: 'Activate',
            };

        case 'off-never-activated':
            return { status: 'Off', action: 'Activate' };

        /**
         * ## Two positions, one sentence, and that is the point
         *
         * `off-nothing-declared` and `off-unknown` are distinct in the
         * derivation and must stay so — one is a fact about the component, the
         * other a fact about a read still in flight. Neither is a fact about
         * the sale. Their old sentences explained the difference to an operator
         * who had not asked, in the vocabulary of checklists and loading
         * states, and one of them sat above a list of member flags saying the
         * component "declares no configuration".
         *
         * They collapse to the same words as `off-never-activated`: this client
         * does not have it. That is the **weakest** claim of the three, so the
         * collapse cannot assert a history the card has not established — which
         * was the whole reason these positions were separated in the first
         * place. The separation survives where it belongs, in the state.
         */
        case 'off-nothing-declared':
        case 'off-unknown':
            return { status: 'Off', action: 'Activate' };
    }
}

/* --------------------------------------------- what the members resolve to - */

/** The middle dot this feature separates fragments with, as `grant-provenance-copy.ts` does. */
const SEPARATOR = ' · ';

/** The members this read can actually speak for. The rest are not claims to make. */
function known(members: readonly ComponentMemberFlag[]) {
    return members.filter((member) => member.known === true);
}

function list(keys: readonly string[]): string {
    return keys.join(', ');
}

/**
 * The component is off and some of its flags are not (`D5`, the reverse
 * direction).
 *
 * The two surfaces are **allowed** to disagree — the component's grant is one
 * grantor among several, and withdrawing it is not the same as turning the
 * capability off. What was missing is the card saying so. Until this, the
 * component card read *off*, the capability was *on*, and only the flags list
 * knew; an operator who deactivated to stop billing a museum for something
 * believed they had.
 *
 * The grantor is named through {@link setByText} — the **Set by** column's own
 * function, not a second wording of it — for the reason that module states for
 * itself (`grant-provenance-copy.ts:14-17`). An operator who reads "This
 * client" here and goes to the flags list must find the same words there.
 *
 * `undefined` rather than an empty string when nothing survives: there is no
 * sentence to render, and a caller that had to test for `''` would eventually
 * render an empty warning box.
 */
export function survivingGrantsNotice(members: readonly ComponentMemberFlag[]): string | undefined {
    const surviving = known(members).filter((member) => member.enabled);

    if (surviving.length === 0) return undefined;

    const holders = [...new Set(surviving.map((member) => setByText(member.provenance)))];
    const plural = surviving.length > 1;

    return (
        `${list(surviving.map((member) => member.key))} ${plural ? 'are' : 'is'} still on for ` +
        `this client even though the component is off, held up by ` +
        `${holders.join(SEPARATOR)}. Turning the component off did not take ` +
        `${plural ? 'them' : 'it'} away.`
    );
}

/**
 * The component is on and some of its flags are not (`D15` rule 2).
 *
 * A component groups flags and shows their state (`D15` rule 4), and the state
 * that matters is the **verdict** — what this museum actually gets — not the
 * grant. A flag can carry this component's grant and still resolve `false`:
 * archived, pinned off by an override, or outside a rollout. The card read
 * "Active" for all three and said nothing, so part of a capability a museum was
 * sold could be silently off with a green badge above it.
 *
 * A member the flags read never carried is **not** reported here. That is a
 * catalog row naming a flag that does not exist, not an entitlement shortfall,
 * and sending an operator to fix the museum would be sending them to the wrong
 * screen.
 */
export function memberShortfallNotice(members: readonly ComponentMemberFlag[]): string | undefined {
    const withheld = known(members).filter((member) => !member.enabled);

    if (withheld.length === 0) return undefined;

    const plural = withheld.length > 1;

    return (
        `This client holds the component, but ${list(withheld.map((member) => member.key))} ` +
        `${plural ? 'do' : 'does'} not resolve on for them — so part of what this component ` +
        `grants is not working. The flags list says what is holding ` +
        `${plural ? 'them' : 'it'} down.`
    );
}

/* ----------------------------------------------------------- turning off - */

export interface DeactivationConfirmCopy {
    readonly title: string;
    readonly description: string;
    /** The consequence that outlives the write. Rendered as a warning, not as body text. */
    readonly warning: string;
    readonly confirmLabel: string;
}

/**
 * The confirmation for withdrawing a client's entitlement.
 *
 * ## Two things propagate, and only one of them is this module's
 *
 * The dialog that uses this must also render the shared `PropagationNotice`,
 * which already says that saved changes take up to 30 seconds to reach running
 * services. That sentence is **not** repeated here on purpose: two wordings of
 * one fact drift apart, and the component is the one every other write screen
 * already shows.
 *
 * What `PropagationNotice` cannot say is the second half, and it is the half
 * that surprises people: the delay is about when the change *arrives*, while a
 * dispute cycle a client has already been admitted to is not reached by it at
 * all. Admission happened under the entitlement that existed at the time, the
 * cycle runs to its end on its own schedule, and cutting the component now does
 * not stop it. An operator who deactivates to stop a cycle, and who is not told
 * this, will watch it continue and conclude the deactivation failed — then go
 * looking for something to undo.
 */
export function deactivationConfirmCopy(displayName: string): DeactivationConfirmCopy {
    return {
        title: `Turn ${displayName} off for this client?`,
        description:
            'Every flag this component grants is withdrawn from the client. The configuration ' +
            'they have already done is kept, so turning it back on later does not start over.',
        warning:
            'A dispute cycle this client has already been admitted to is not stopped by this. ' +
            'It was admitted under the entitlement they held at the time and runs to its end ' +
            'on its own schedule.',
        confirmLabel: 'Deactivate',
    };
}

/* ------------------------------------------------ the half-finished yes - */

export interface SeedIncompleteCaveat {
    readonly title: string;
    /** The API's own sentence, which names how many settings were missed. */
    readonly body: string;
    readonly detail: string;
}

/**
 * The activation that landed with a short checklist.
 *
 * Rendered beside the card that is now **on**, as a caveat and never as an
 * error, because that is what it is: the museum is entitled, every flag was
 * granted and the product works for them today. Only the configuration rows the
 * activation tried to seed are missing. The API chose a 200 for exactly this
 * reason, and an operator told this in red would back out an entitlement that
 * was fine — which withdraws a component the customer has already been sold.
 *
 * The API's message is passed through rather than rebuilt: it names how many
 * settings were missed, and there is no structured count in the body to count
 * from. It may be empty, which is why {@link SeedIncompleteCaveat.detail} says
 * what to do rather than leaving that to the server's wording.
 */
export function seedIncompleteCaveat(message: string): SeedIncompleteCaveat {
    return {
        title: 'Activated — the checklist is short',
        body: message,
        detail:
            'The client holds every flag in this component and it works for them now. Only some ' +
            'of the configuration checklist below was not created. Activating again is safe and ' +
            'seeds whatever is still missing.',
    };
}
